import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputText} from "primereact/inputtext";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import DescriptionDialogFields from "../../field/document/DescriptionDialogFields";
import {TabPanel, TabView} from "primereact/tabview";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	CustomDescription,
	Description,
	DescriptionCustom,
	DescriptionCustomInterface,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxDescriptionDescription
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface DescriptionDialogStatus extends PortalDialogState<ToolboxDescriptionDescription> {
	key: string,
	value: string,
	panel: number
}

export class DescriptionDialogDefinition extends PortalDialog<ToolboxDescriptionDescription,
	PortalDialogProperties<ToolboxDescriptionDescription>, DescriptionDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<DescriptionDialogFields, string>(),
			key: "",
			value: "",
			panel: 0
		};

		this.getCustomField = this.getCustomField.bind(this);
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.pdfaConversion, Functions.getChangeDocumentSettingsDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.DESCRIPTION, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private getCustomField(key: string): DescriptionCustom | null {
		let parameter: ToolboxDescriptionDescription | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let customFields: Array<DescriptionCustom> = FunctionHelper.applyValue(
			parameter?.custom, ToolboxDescriptionDescription.getCustomDefault()
		);
		for (let customField of customFields) {
			if (customField.key === key) {
				return customField;
			}
		}

		return null;
	}

	public renderGeneralTab(parameter: ToolboxDescriptionDescription): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.TITLE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-title">
							<Translate {...this.props} data="description.general.title" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-title"
									   value={FunctionHelper.applyValue(
										   parameter.title, ToolboxDescriptionDescription.getTitleDefault()
									   )}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.TITLE, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.AUTHOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-author">
							<Translate {...this.props} data="description.general.author" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-author"
									   value={FunctionHelper.applyValue(
										   parameter.author, ToolboxDescriptionDescription.getAuthorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.AUTHOR, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.SUBJECT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-subject">
							<Translate {...this.props} data="description.general.subject" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   parameter.subject, ToolboxDescriptionDescription.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.KEYWORDS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-keywords">
							<Translate {...this.props} data="description.general.keywords" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-keywords"
									   value={FunctionHelper.applyValue(
										   parameter.keywords, ToolboxDescriptionDescription.getKeywordsDefault()
									   )}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.KEYWORDS, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.CREATOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creator">
							<Translate {...this.props} data="description.extended.creator" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creator"
									   value={FunctionHelper.applyValue(
										   parameter.creator, ToolboxDescriptionDescription.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.CREATOR, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.PRODUCER)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-producer">
							<Translate {...this.props} data="description.extended.producer" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-producer"
									   value={FunctionHelper.applyValue(
										   parameter.producer, ToolboxDescriptionDescription.getProducerDefault()
									   )}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.PRODUCER, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderCustomTab(parameter: ToolboxDescriptionDescription): ReactNode {
		let errors: Map<DescriptionDialogFields, string> = this.state.fieldErrors;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.KEY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-key">
							<Translate {...this.props} data="description.custom.key" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-key" value={this.state.key}
									   className={errors.has(DescriptionDialogFields.KEY) ? "p-invalid" : ""}
									   tooltip={errors.has(DescriptionDialogFields.KEY) ? Translate.value(
										   errors.get(DescriptionDialogFields.KEY)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.KEY, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(DescriptionDialogFields.KEY) ? Translate.value(
								errors.get(DescriptionDialogFields.KEY)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(DescriptionDialogFields.VALUE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-value">
							<Translate {...this.props} data="description.custom.value" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-value" value={this.state.value}
									   className={errors.has(DescriptionDialogFields.VALUE) ? "p-invalid" : ""}
									   tooltip={errors.has(DescriptionDialogFields.VALUE) ? Translate.value(
										   errors.get(DescriptionDialogFields.VALUE)!, {ns: "message"}
									   ) : ""}
									   onInput={(e) => this.onChange(
										   DescriptionDialogFields.VALUE, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(DescriptionDialogFields.VALUE) ? Translate.value(
								errors.get(DescriptionDialogFields.VALUE)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5"/>
						<div className="p-col-12 p-sm-7 p-grid">
							<Button className="dialog-button" disabled={errors.size > 0}
									label={
										Translate.value(this.getCustomField(this.state.key) === null ?
											'buttons.add' : 'buttons.change')
									}
									icon={
										"icon" + (this.getCustomField(this.state.key) === null ?
											" icon-add" : " icon-cog")
									}
									onClick={() => this.onChange(DescriptionDialogFields.CUSTOM, true)}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"/>
				</fieldset>

				{this.renderCustomFieldList(parameter)}
			</>
		);
	}

	private renderCustomFieldList(parameter: ToolboxDescriptionDescription): ReactNode {
		let customFields: Array<ReactNode> = [];
		let errors: Map<DescriptionDialogFields, string> = this.state.fieldErrors;

		for (let customField of FunctionHelper.applyValue(
			parameter.custom, ToolboxDescriptionDescription.getCustomDefault()
		)) {
			if (customField.remove) {
				continue;
			}

			customFields.push(
				<div className="custom-field" key={customField.key} onClick={() => {
					errors.delete(DescriptionDialogFields.KEY);
					errors.delete(DescriptionDialogFields.VALUE);

					this.setState({
						key: FunctionHelper.applyValue(customField.key, DescriptionCustom.getKeyDefault()),
						value: FunctionHelper.applyValue(customField.value, DescriptionCustom.getValueDefault())
					});
				}}>
					<div className="field-key">{customField.key}</div>
					<div className="field-value">{customField.value}</div>
					<div className="field-functions">
						<Button icon="icon icon-delete small" onClick={() => {
							customField.remove = true;

							this.setState({
								parameter: this.state.parameter
							});
						}}/>
					</div>
				</div>
			);
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={customFields.length === 0}>
					<div className="p-col-12 p-grid">
						<div className="custom-fields-list">
							<div className="custom-fields-header">
								<div className="field-key">
									<Translate {...this.props} data="description.custom.key" namespace="dialog"/>
								</div>
								<div className="field-value">
									<Translate {...this.props} data="description.custom.value" namespace="dialog"/>
								</div>
								<div className="field-functions"/>
							</div>
							<div className="custom-fields">
								{customFields}
							</div>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxDescriptionDescription | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<div className="p-col-12 p-grid">
					<TabView activeIndex={this.state.panel}
							 onTabChange={(e) => this.setState({panel: e.index})}>
						<TabPanel header={Translate.value("description.general.label", {ns: "dialog"})}
								  headerClassName="control-tab-general" contentClassName="general-tab-panel">
							{this.renderGeneralTab(parameter!)}
						</TabPanel>
						<TabPanel header={Translate.value("description.custom.label", {ns: "dialog"})}
								  headerClassName="control-tab-custom" contentClassName="custom-tab-panel"
								  headerStyle={
									  this.state.fields?.contains(DescriptionDialogFields.CUSTOM) ?
										  {} : {display: "none"}
								  }>
							{this.state.fields?.contains(DescriptionDialogFields.CUSTOM) &&
								this.renderCustomTab(parameter!)
							}
						</TabPanel>
					</TabView>
				</div>
			</>
		)
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["description-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.description.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: DescriptionDialogFields, value: any): void {
		let parameter: ToolboxDescriptionDescription = this.state.parameter!.data;
		let errors: Map<DescriptionDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case DescriptionDialogFields.TITLE:
				parameter.title = value;
				break;
			case DescriptionDialogFields.AUTHOR:
				parameter.author = value;
				break;
			case DescriptionDialogFields.SUBJECT:
				parameter.subject = value;
				break;
			case DescriptionDialogFields.KEYWORDS:
				parameter.keywords = value;
				break;
			case DescriptionDialogFields.CREATOR:
				parameter.creator = value;
				break;
			case DescriptionDialogFields.PRODUCER:
				parameter.producer = value;
				break;
			case DescriptionDialogFields.KEY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				this.setState({
					key: value
				});
				return;
			case DescriptionDialogFields.VALUE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				this.setState({
					value: value
				});
				return;
			case DescriptionDialogFields.CUSTOM:
				if (!FunctionHelper.exists(this.state.key) || FunctionHelper.isEmpty(this.state.key)) {
					errors.set(DescriptionDialogFields.KEY, "errors.fields.set");
					break;
				}

				if (!FunctionHelper.exists(this.state.value) || FunctionHelper.isEmpty(this.state.value)) {
					errors.set(DescriptionDialogFields.VALUE, "errors.fields.set");
					break;
				}

				let isNew = true;
				let customFields: Array<DescriptionCustom> = FunctionHelper.applyValue(
					parameter.custom, ToolboxDescriptionDescription.getCustomDefault()
				);
				for (let customField of customFields) {
					if (customField.key === this.state.key) {
						customField.value = this.state.value;
						customField.remove = false;
						isNew = false;
						break;
					}
				}

				if (isNew) {
					customFields.push(DescriptionCustom.fromJson({
						key: this.state.key,
						value: this.state.value,
						remove: false
					} as DescriptionCustomInterface));
				}

				parameter.custom = customFields;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<span className="icon icon-list"/>
					<Translate {...this.props} data="description.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxDescriptionDescription | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.author)) {
			let authors: Array<string> = [];
			for (let author of portalDocument.metadata!.information.description.author!) {
				authors.push(author.name!)
			}
			parameter!.author = authors.join(";")
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.keywords)) {
			let keywords: Array<string> = [];
			for (let keyword of portalDocument.metadata!.information.description.keywords!) {
				keywords.push(keyword.keyword!)
			}
			parameter!.keywords = keywords.join(";")
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.application)) {
			parameter!.creator = portalDocument.metadata!.information.description.application;
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.producer)) {
			parameter!.producer = portalDocument.metadata!.information.description.producer;
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.subject)) {
			parameter!.subject = portalDocument.metadata!.information.description.subject;
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.title)) {
			parameter!.title = portalDocument.metadata!.information.description.title;
		}

		if (FunctionHelper.exists(portalDocument.metadata?.information?.description?.custom)) {
			let customFields: Array<CustomDescription> = FunctionHelper.applyValue(
				portalDocument.metadata?.information?.description?.custom, Description.getCustomDefault()
			);
			parameter!.custom = [];

			for (let customField of customFields) {
				parameter!.custom.push(DescriptionCustom.fromJson({
					key: customField.key,
					value: customField.value,
					remove: false
				} as DescriptionCustomInterface));
			}
		}

		this.setState({
			panel: 0,
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone()
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			description: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.description", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.DESCRIPTION;
	}

	protected getDefaultParameter(): Parameter<ToolboxDescriptionDescription> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		});
	}
}

const DescriptionDialog = React.memo(DescriptionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxDescriptionDescription>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxDescriptionDescription>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DescriptionDialog;